/**
 * Did a letter we recorded actually reach the homeowner? (#555)
 *
 * A composed letter is recorded on the thread whether or not it went out — in every
 * environment but production, and whenever an org switch is off, the send is GATED and the
 * row is only a record. Without a marker, that record reads exactly like a delivered email,
 * which is how staff come to believe a homeowner was told. The same distinction the
 * auto-reply banner draws (#595): sent, test mailbox, held.
 *
 * Stored per message (`messages.delivery_status` / `delivery_note`, migration 272). NULL for
 * inbound mail and for outbound rows written before the column existed — no marker for those.
 */

import type { InboxAutoReply } from '@/services/data-service';

export type DeliveryStatus = 'sent' | 'test_mailbox' | 'held';

export interface DeliveryRecord {
	status: DeliveryStatus;
	/** The gate reason for `held`, or the mailbox it went to for `test_mailbox`. */
	note: string | null;
}

/** Just the fields `deliveryFromOutcome` reads — a subset of `SendEmailResult`. */
export interface SendOutcome {
	sent: boolean;
	skipped?: boolean;
	sunkTo?: string;
	error?: string;
}

/**
 * What to record for a send that did not FAIL. A transport failure records nothing at all,
 * so it has no delivery state to write.
 */
export function deliveryFromOutcome(outcome: SendOutcome): DeliveryRecord {
	if (outcome.sent && outcome.sunkTo) return { status: 'test_mailbox', note: outcome.sunkTo };
	if (outcome.sent) return { status: 'sent', note: null };
	return { status: 'held', note: outcome.error ?? 'Outbound email is switched off.' };
}

/**
 * A recorded letter's delivery, in the auto-reply banner's vocabulary (#595) — so there is ONE
 * delivery UI: `test_mailbox` → `sunk`, `held` → `held`. Null for a delivered letter, inbound
 * mail and legacy rows: a PM-composed letter that reached the homeowner is the normal case and
 * wears no marker, unlike an auto-reply, which is always shown because nobody composed it.
 */
export function letterDeliveryAttempt(message: {
	deliveryStatus?: string | null;
	deliveryNote?: string | null;
	toAddress?: string | null;
}): InboxAutoReply | null {
	const outcome = message.deliveryStatus === 'test_mailbox' ? 'sunk'
		: message.deliveryStatus === 'held' ? 'held'
			: null;
	if (!outcome) return null;
	return {
		outcome,
		ruleName: null,
		templateId: null,
		templateName: null,
		to: message.toAddress ? [message.toAddress] : [],
		sunkTo: outcome === 'sunk' ? message.deliveryNote ?? null : null,
		reason: outcome === 'held' ? message.deliveryNote ?? null : null,
		replyMessageId: null,
		// Null, not the letter's date: the banner's detail line then carries `summary` — the part
		// that matters — and the message already shows when it was written.
		at: null,
		summary: 'This copy is only a record.',
	};
}

/**
 * Which delivery banner a message shows — never two that contradict each other.
 *
 * Each state only means something in ONE direction. A letter's own delivery is written on the
 * OUTBOUND row we recorded; an auto-reply record is keyed to the INBOUND message it answered.
 * So an outbound message shows its letter state and ignores any auto-reply record (which could
 * only be a misattribution), and an inbound message shows its auto-replies and ignores a
 * delivery column it can never legitimately carry.
 */
export function deliveryBannerFor(message: {
	direction?: number | null;
	deliveryStatus?: string | null;
	deliveryNote?: string | null;
	toAddress?: string | null;
	autoReply?: InboxAutoReply | null;
	autoReplies?: InboxAutoReply[] | null;
}): { noun: 'Letter' | 'Auto-reply'; attempts: InboxAutoReply[] } | null {
	if (Number(message.direction) === 0) {
		const letter = letterDeliveryAttempt(message);
		return letter ? { noun: 'Letter', attempts: [letter] } : null;
	}
	const attempts = message.autoReplies ?? (message.autoReply ? [message.autoReply] : []);
	return attempts.length ? { noun: 'Auto-reply', attempts } : null;
}
